import {
  FC,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  FetchedReportData,
  ReportStatusType,
  SelectOption,
} from "../../../types";
import useFetchWithAuth from "../../hooks/useFetchWithAuth";
import { useToast } from "../../../context/ToastContext";
import CaseReportCard from "./CaseReportCard";
import ReportSort from "./ReportSort";
import { getFileNameTimeStamp } from "../../../utils/dateUtils";
import { csvHeaderOptions } from "../../SubmitTip/resources";
import CaseReportCardSkeleton from "../../Skeletons/CaseReportCardSkeleton";
import ReportViewCard from "../../SubmitTip/ReportViewCard";
import DeleteConfirmationModal from "../../modals/DeleteConfirmationModal";
import UserReportPDFTemplate from "../../../utils/UserReportPDFTemplate";
import { getApiUrl } from "../../../utils/api";
import { getUserRole } from "../../../utils/authHelpers";
import { filteredReportDataForCSV } from "../../../utils/csvUtils";
import SearchFilter from "../../common/SearchFilter";
import { searchReportProperties } from "../../../utils/filterUtils";
import { FaArrowLeft } from "react-icons/fa6";
import { FaExclamationTriangle } from "react-icons/fa";
import noReportImg from "../../../images/no-reports.png";

interface FilterExportResultsSectionProps {
  activeMode: "filter" | "export" | "reportDetails";
  reports: FetchedReportData[] | undefined;
  filteredReports: FetchedReportData[] | undefined;
  reportDetails: FetchedReportData | undefined;
  setReportDetails: (reportDetails: FetchedReportData | undefined) => void;
  loading: boolean;
  error: string | null;
  setFilteredReports: Dispatch<SetStateAction<FetchedReportData[] | undefined>>;
  handleStatusUpdate: (reportId: string, newStatus: ReportStatusType) => void;
}

const FilterExportResultsSection: FC<FilterExportResultsSectionProps> = ({
  activeMode,
  reports,
  filteredReports,
  setFilteredReports,
  reportDetails,
  setReportDetails,
  loading,
  error,
  handleStatusUpdate,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { fetchWithAuth } = useFetchWithAuth();
  const isSuperAdmin = getUserRole() === "super_admin";

  // State management
  const [selectedReports, setSelectedReports] = useState<FetchedReportData[]>(
    []
  );
  const [sortOption, setSortOption] = useState<SelectOption | null>(null);
  const [statusFilter, setStatusFilter] = useState<SelectOption | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter and sort reports
  useEffect(() => {
    if (!reports) return;

    // Filter reports based on authority
    let sortedReports = reports.filter(
      (report) => !(report.abuserDetails?.hasSpecialAuthority && !isSuperAdmin)
    );

    // Apply search filter
    if (debouncedSearchQuery.trim() !== "") {
      sortedReports = sortedReports.filter((report) =>
        searchReportProperties(report, debouncedSearchQuery)
      );
    }

    // Apply sorting
    if (sortOption?.value === "most recent") {
      sortedReports.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sortOption?.value === "today") {
      const today = new Date().toLocaleDateString();
      sortedReports = sortedReports.filter(
        (report) => new Date(report.createdAt).toLocaleDateString() === today
      );
    } else if (sortOption?.value === "this week") {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      sortedReports = sortedReports.filter(
        (report) =>
          new Date(report.createdAt).getTime() >= startOfWeek.getTime()
      );
    }

    // Apply status filter
    if (statusFilter?.value && statusFilter.value !== "") {
      sortedReports = sortedReports.filter(
        (report) => report.status === statusFilter.value
      );
    }

    setFilteredReports(sortedReports);
  }, [
    reports,
    sortOption,
    statusFilter,
    debouncedSearchQuery,
    isSuperAdmin,
    setFilteredReports,
  ]);

  // Handler functions
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!filteredReports) return;

    setSelectedReports((prevSelected) =>
      prevSelected.length === filteredReports.length ? [] : [...filteredReports]
    );
  }, [filteredReports]);

  const confirmDelete = useCallback((id: string) => {
    setReportToDelete(id);
    setDeleteModalOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!reportToDelete || !filteredReports || !reports) return;

    // Optimistically remove the report from the UI
    setFilteredReports((prevReports) =>
      prevReports?.filter((report) => report._id !== reportToDelete)
    );

    // Remove from selected reports if it was selected
    setSelectedReports((prevSelected) =>
      prevSelected.filter((report) => report._id !== reportToDelete)
    );

    try {
      await fetchWithAuth(getApiUrl(`/admin/reports/${reportToDelete}`), {
        method: "DELETE",
      });

      toast({
        type: "success",
        title: "Success",
        subtitle: "Report deleted successfully!",
      });
    } catch (error) {
      // Revert the optimistic deletion if there's an error
      const deletedReport = reports.find(
        (report) => report._id === reportToDelete
      ) as FetchedReportData;

      if (deletedReport) {
        setFilteredReports((prevReports) =>
          prevReports ? [...prevReports, deletedReport] : [deletedReport]
        );

        setSelectedReports((prevSelected) => {
          const wasSelected = prevSelected.some(
            (report) => report._id === reportToDelete
          );
          return wasSelected ? [...prevSelected, deletedReport] : prevSelected;
        });
      }

      toast({
        type: "error",
        title: "Error",
        subtitle: "Error deleting report!",
      });
    } finally {
      setDeleteModalOpen(false);
      setReportToDelete(null);
    }
  }, [
    reportToDelete,
    filteredReports,
    reports,
    fetchWithAuth,
    toast,
    setFilteredReports,
  ]);

  const handleBackToReports = useCallback(() => {
    setReportDetails(undefined);
    navigate("/admin/reports");
  }, [navigate, setReportDetails]);

  // Render report details view
  if (reportDetails && reportDetails._id) {
    return (
      <div className="flex flex-col items-start gap-4 w-full lg:w-[672px] pt-5 h-md:pt-10">
        <button
          className="flex h-[32px] items-center gap-2 py-1.5 px-4 font-semibold text-base leading-5"
          onClick={handleBackToReports}
        >
          <FaArrowLeft />
          Back
        </button>
        <div
          id="export-content"
          className="w-full flex flex-col flex-grow overflow-y-auto"
        >
          <ReportViewCard
            onStatusUpdate={handleStatusUpdate}
            report={reportDetails}
            className="w-full h-full"
          />
        </div>

        <div className="flex justify-center w-full py-3">
          <PDFDownloadLink
            document={<UserReportPDFTemplate report={reportDetails} />}
            fileName={`Report-form-Unspoken_${new Date().toISOString()}.pdf`}
            className="h-[52px] self-center text-[18px] leading-[22px] font-extrabold bg-danger text-white py-[15px] px-[92px] rounded-3xl no-underline"
          >
            Export to PDF
          </PDFDownloadLink>
        </div>
      </div>
    );
  }

  // Render filters and report list
  return (
    <div className="flex flex-col pt-5 h-md:pt-10 w-full lg:w-[672px]">
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />

      {/* Search and filter controls */}
      <div className="flex flex-col w-full justify-between items-start">
        <div className="w-full mb-4">
          <SearchFilter onSearch={handleSearch} />
        </div>

        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex w-full justify-between">
            <div className="flex w-full justify-start gap-4">
              <ReportSort
                setSortOption={setSortOption}
                disabled={
                  loading || !!error || !reports || reports.length === 0
                }
              />
              <ReportSort
                type="filter"
                setSortOption={setStatusFilter}
                disabled={
                  loading || !!error || !reports || reports.length === 0
                }
              />
            </div>

            {activeMode === "export" && (
              <button
                className="py-1.5 px-4 text-gray-7 font-[600] text-base leading-5"
                onClick={handleSelectAll}
              >
                Select All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Report count */}
      {reports && reports.length > 0 && (
        <p className="w-full text-right text-gray-5 font-[400] text-[10px] leading-3 pr-[18px]">
          {filteredReports?.length || 0} Reports
        </p>
      )}

      {/* Report list */}
      <div className="flex flex-col flex-grow overflow-y-auto pr-[9px] mr-[9px] mt-4">
        {/* Loading state */}
        {loading && (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <CaseReportCardSkeleton
                selectable={activeMode === "export"}
                key={i}
              />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col gap-5 h-full justify-center items-center">
            <FaExclamationTriangle color="#FF0D3C" size={200} />
            <h3 className="font-extrabold text-[26px] leading-8 text-gray-3">
              Error Encountered!
            </h3>
            <p className="font-normal text-sm leading-[18px] text-gray-3 max-w-[406px] text-center">
              Unfortunately, we encountered an error while loading reports.
              Please bear with us and try again.
            </p>
          </div>
        )}

        {/* Empty state */}
        {!loading &&
          !error &&
          (!filteredReports || filteredReports.length === 0) && (
            <div className="flex flex-col gap-5 h-full justify-center items-center">
              <img
                src={noReportImg}
                alt="No reports"
                className="w-[154px] h-[144px]"
              />
              <h3 className="font-extrabold text-[26px] leading-8 text-gray-3">
                No reports found!
              </h3>
              <p className="font-normal text-sm leading-[18px] text-gray-3">
                Reports will show up here as users submit them
              </p>
            </div>
          )}

        {/* Report list */}
        {!loading &&
          !error &&
          filteredReports &&
          filteredReports.length > 0 && (
            <div className="flex flex-col gap-4">
              {filteredReports.map((report) => (
                <CaseReportCard
                  key={report._id}
                  report={report}
                  onDelete={() => confirmDelete(report._id)}
                  selected={selectedReports.some(
                    (selected) => selected._id === report._id
                  )}
                  selectable={activeMode === "export"}
                  setReportDetails={setReportDetails}
                  onSelect={() => {
                    setSelectedReports((prevSelected) => {
                      const isAlreadySelected = prevSelected.some(
                        (selected) => selected._id === report._id
                      );
                      return isAlreadySelected
                        ? prevSelected.filter(
                            (selected) => selected._id !== report._id
                          )
                        : [...prevSelected, report];
                    });
                  }}
                />
              ))}
            </div>
          )}
      </div>

      {/* Export button */}
      {!loading && !error && (
        <div className="flex justify-center w-full py-3">
          <CSVLink
            data={filteredReportDataForCSV(selectedReports)}
            headers={csvHeaderOptions}
            filename={`Report-form-Unspoken_${getFileNameTimeStamp()}.csv`}
            className="self-center"
          >
            <button
              disabled={!selectedReports || selectedReports.length === 0}
              className="w-[330px] font-extrabold text-lg leading-[22px] text-center bg-success-dark text-white disabled:bg-gray-3 disabled:text-gray-4 disabled:cursor-not-allowed rounded-[24px] py-[15px] px-[72.5px]"
            >
              Export to CSV
            </button>
          </CSVLink>
        </div>
      )}
    </div>
  );
};

export default FilterExportResultsSection;
